// src/components/home/modules/day-1/Day1.jsx
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useOperatorActions } from "../../../../utils/useOperatorActions";
import CyberBoxComponent from "../../../shared/ui/layout/CyberBoxComponent";
import { AhangariHoldingLogoDisplayComponent } from "../../../shared/ui/logo-display/ahangariHoldingLogoDisplayComponent";
import { HotLogoDisplayComponent } from "../../../shared/ui/logo-display/HotLogoDisplayComponent";
import { WelcomeComponent } from "./components/welcomeComponent";
import MondfxLogoDisplayComponent from "../../../shared/ui/logo-display/MondfxLogoDisplayComponent";
import FenefxLogoDisplayComponent from "../../../shared/ui/logo-display/FenefxLogoDisplayComponent";
import FirtChallangeLeaderBoardTableComponent from "./components/FirtChallangeLeaderBoardTableComponent";
import PreviousChallangeLeaderBoardTableComponent from "./components/PreviousChallangeLeaderBoardTableComponent";
import ResultTableComponent from "./components/ResultTableComponent";
import Challengs from "./components/Challengs";
import { usePlayersStore } from "../../../../store/playersStore";
import axios from "axios";
import { API_ENDPOINT } from "../../../../lib/api-url";
import CapitanPage from "../day-2/components/capitans/CapitanPage";

const DEFAULT_AVATAR = "/avatar/avatar-default.webp";

export function Day1() {
  const [currentHtml, setCurrentHtml] = useState(null);
  const TABLE_DELAY_MS = 3000;
  const [canReveal, setCanReveal] = useState(false);
  const [showTablesNow, setShowTablesNow] = useState(false);
  const [tablesSessionKey, setTablesSessionKey] = useState(0);
  const revealTimerRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const upsert = usePlayersStore.getState().upsert;
  const [initialLoaded, setInitialLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(API_ENDPOINT.users.getFinalistsData);
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        for (const it of items) {
          const login = Number(it?.account_login);
          if (!Number.isFinite(login)) continue;
          const displayName =
            `${it?.first_name ?? ""} ${it?.last_name ?? ""}`.trim() ||
            it?.username ||
            String(login);
          upsert({
            login,
            username: displayName,
            equity: Number(it?.equity ?? 0),
            balance: Number(it?.balance ?? 0),
            profileImage: DEFAULT_AVATAR,
          });
        }
        setInitialLoaded(true);
      } catch {
        setInitialLoaded(true);
      }
    })();
  }, [upsert]);

  const clearRevealTimer = () => {
    if (revealTimerRef.current) {
      clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  };
  const prepareLayer = (mode) => {
    clearRevealTimer();
    setTablesSessionKey((k) => k + 1);
    setCanReveal(false);
    setShowTablesNow(false);
    setCurrentHtml(mode);
  };
  const startTableReveal = (mode) => {
    prepareLayer(mode);
    revealTimerRef.current = setTimeout(() => {
      setCanReveal(true);
      revealTimerRef.current = null;
    }, TABLE_DELAY_MS);
  };
  const startTableRevealWaitForData = (mode) => {
    prepareLayer(mode);
  };
  useOperatorActions("SHOW_AHANGARI_HOLDING", () => {
    clearRevealTimer();
    setCurrentHtml("AHANGARI_HOLDING_LOGO_DISPLAY");
  });
  useOperatorActions("SHOW_HOT_LOGO", () => {
    clearRevealTimer();
    setCurrentHtml("SHOW_HOT_LOGO");
  });
  useOperatorActions("SHOW_INTRO", () => {
    clearRevealTimer();
    setCurrentHtml("SHOW_INTRO");
  });
  useOperatorActions("SHOW_MOND_LOGO", () => {
    clearRevealTimer();
    setCurrentHtml("SHOW_MOND_LOGO");
  });
  useOperatorActions("SHOW_FENE_LOGO", () => {
    clearRevealTimer();
    setCurrentHtml("SHOW_FENE_LOGO");
  });
  useOperatorActions("SHOW_CHALLENGES", () => {
    clearRevealTimer();
    setCurrentHtml("SHOW_CHALLENGES");
  });
  useOperatorActions("SHOW_TABLE", () =>
    startTableRevealWaitForData("SHOW_TABLE")
  );
  useOperatorActions("SHOW_TABLE_Realtime", () =>
    startTableReveal("SHOW_TABLE_Realtime")
  );
  useOperatorActions("SHOW_TABLE_Final", () =>
    startTableReveal("SHOW_TABLE_Final")
  );
  useOperatorActions("SHOW_ALL_CAPTAINS", () =>
    setCurrentHtml("SHOW_ALL_CAPTAINS")
  );
  useEffect(() => () => clearRevealTimer(), []);
  const variants = {
    initial: { opacity: 0, scale: 0.96, y: 12, filter: "blur(6px)" },
    animate: { opacity: 1, scale: 1, y: 0, filter: "blur(0px)" },
    exit: { opacity: 0, scale: 0.98, y: -8, filter: "blur(3px)" },
  };
  const renderTableLayer = (children, welcomeText) => (
    <div className="relative w-full flex items-center justify-center">
      <motion.div
        key={`tables-${tablesSessionKey}`}
        initial={{ opacity: 0, y: 8, scale: 0.98, filter: "blur(3px)" }}
        animate={
          showTablesNow
            ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
            : { opacity: 0, y: 8, scale: 0.98, filter: "blur(3px)" }
        }
        transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
        aria-hidden={!showTablesNow}
        className={`w-full flex justify-center ${
          showTablesNow ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={
          showTablesNow
            ? {}
            : { position: "absolute", inset: 0, visibility: "hidden" }
        }
      >
        {children}
      </motion.div>
      <AnimatePresence
        onExitComplete={() => setShowTablesNow(true)}
        mode="wait"
      >
        {!canReveal && (
          <motion.div
            key="welcome-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <WelcomeComponent text={welcomeText} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
  const renderCurrentHtml = (name) => {
    switch (name) {
      case "AHANGARI_HOLDING_LOGO_DISPLAY":
        return <AhangariHoldingLogoDisplayComponent />;
      case "SHOW_HOT_LOGO":
        return <HotLogoDisplayComponent />;
      case "SHOW_MOND_LOGO":
        return <MondfxLogoDisplayComponent />;
      case "SHOW_FENE_LOGO":
        return <FenefxLogoDisplayComponent />;
      case "SHOW_INTRO":
        return <WelcomeComponent />;
      case "SHOW_CHALLENGES":
        return <Challengs />;
      case "SHOW_TABLE":
        return renderTableLayer(
          <div className="w-full flex flex-col lg:flex-row gap-8 items-start justify-center">
            <PreviousChallangeLeaderBoardTableComponent
              onDataReady={() => setCanReveal(true)}
              play={showTablesNow}
            />
          </div>,
          "رده بندی مرحله سوم"
        );
      case "SHOW_TABLE_Realtime":
        return renderTableLayer(
          <div className="w-full flex flex-col lg:flex-row gap-8 items-start justify-center">
            {initialLoaded && (
              <FirtChallangeLeaderBoardTableComponent play={showTablesNow} />
            )}
          </div>,
          "رده بندی انفرادی"
        );
      case "SHOW_TABLE_Final":
        return renderTableLayer(
          <div className="w-full flex flex-col lg:flex-row gap-8 items-start justify-center">
            <ResultTableComponent />
          </div>,
          "رده بندی نهایی"
        );
      case "SHOW_ALL_CAPTAINS":
        return <CapitanPage />;
      default:
        return (
          <div className="w-full h-[600px] text-white flex items-center justify-center">
            no item
          </div>
        );
    }
  };
  return currentHtml === "SHOW_CHALLENGES" ||
    currentHtml === "SHOW_ALL_CAPTAINS" ? (
    renderCurrentHtml(currentHtml)
  ) : (
    <CyberBoxComponent>
      <div className="w-screen h-screen flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentHtml ?? "empty"}
            variants={reduceMotion ? undefined : variants}
            initial={reduceMotion ? false : "initial"}
            animate={reduceMotion ? undefined : "animate"}
            exit={reduceMotion ? undefined : "exit"}
            className="w-screen px-8"
            transition={{ duration: 0.6 }}
          >
            {renderCurrentHtml(currentHtml)}
          </motion.div>
        </AnimatePresence>
      </div>
    </CyberBoxComponent>
  );
}
